import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import LeaveViewController from "../../redux/actions/leaveApiAction";
import FormDrawer from "../../components/layout/FormDrawer";
import MobileSelectField from "../../components/mobile/MobileSelectField";
import {
  primaryButtonSx,
  inputSx,
  mobileInputSx,
  BRAND,
} from "../../components/auth/authTheme";
import { getUserInfoFromToken } from "../../utils/authUtils";

const LEAVE_TYPE_OPTIONS = [
  { value: "paid", label: "Paid Leave" },
  { value: "unpaid", label: "Unpaid Leave" },
  { value: "sick", label: "Sick Leave" },
];

const SESSION_OPTIONS = [
  { value: "1", label: "Session 1 (Morning)" },
  { value: "2", label: "Session 2 (Afternoon)" },
];

const SectionCard = ({ title, icon, isMobile, children, mb = 1.5 }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: isMobile ? "14px" : 0,
      border: isMobile ? `1px solid ${BRAND.border}` : "none",
      p: isMobile ? 1.5 : 0,
      mb: isMobile ? mb : 3,
      boxShadow: isMobile ? "0 1px 6px rgba(26, 43, 74, 0.04)" : "none",
    }}
  >
    {title && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
        {icon && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              bgcolor: BRAND.orangeLight,
              color: BRAND.orangeHover,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="subtitle2"
          sx={{ color: BRAND.navy, fontWeight: 700, fontSize: "0.9375rem" }}
        >
          {title}
        </Typography>
      </Box>
    )}
    {children}
  </Box>
);

function AutoCalculateLeaveDays() {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (
      values.leave_start_date &&
      values.leave_end_date &&
      values.start_session &&
      values.end_session
    ) {
      const start = new Date(values.leave_start_date);
      const end = new Date(values.leave_end_date);

      if (start > end) {
        setFieldValue("total_leave_days", 0);
        return;
      }

      const dayDiff = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      let total = 0;

      if (dayDiff === 0) {
        total = values.start_session === values.end_session ? 0.5 : 1;
      } else {
        total = dayDiff + 1;
        if (values.start_session === "2") total -= 0.5;
        if (values.end_session === "1") total -= 0.5;
      }

      setFieldValue("total_leave_days", total);
    }
  }, [
    values.leave_start_date,
    values.leave_end_date,
    values.start_session,
    values.end_session,
    setFieldValue,
  ]);

  return null;
}

const validationSchema = Yup.object({
  leave_type: Yup.string().required("Leave type is required"),
  leave_start_date: Yup.date().required("Start date is required"),
  leave_end_date: Yup.date()
    .required("End date is required")
    .min(Yup.ref("leave_start_date"), "End date cannot be before start date"),
  reason: Yup.string()
    .trim()
    .required("Reason is required")
    .min(10, "Reason must be at least 10 characters"),
});

function LeaveRequestFormModal({ show, handleClose }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const gridSpacing = isMobile ? 1.5 : 2.5;

  const { addLeaves } = LeaveViewController();
  const { isLoding } = useSelector((state) => state.leave);

  const [userType, setUserType] = useState(null);
  const [userId, setUserId] = useState(null);

  const [initialValues, setInitialValues] = useState({
    employee: "",
    leave_type: "paid",
    leave_start_date: "",
    leave_end_date: "",
    total_leave_days: "",
    reason: "",
    approval_status: "pending",
    attachments: [],
    start_session: "1",
    end_session: "2",
  });

  useEffect(() => {
    const { userType: role, userId: id } = getUserInfoFromToken();
    setUserType(role);
    setUserId(id);
  }, []);

  useEffect(() => {
    if (!show) return;
    setInitialValues({
      employee: (userType || "").toLowerCase() === "employee" ? userId : "",
      leave_type: "paid",
      leave_start_date: "",
      leave_end_date: "",
      total_leave_days: "",
      reason: "",
      approval_status: "pending",
      attachments: [],
      start_session: "1",
      end_session: "2",
    });
  }, [show, userType, userId]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (key !== "attachments") {
          formData.append(key, value);
        }
      });
      if (values.attachments?.length) {
        Array.from(values.attachments).forEach((file) => {
          formData.append("attachments", file);
        });
      }
      await dispatch(addLeaves(formData));
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error submitting leave form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderFooter = (isSaving, submitForm) =>
    isMobile ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isSaving}
          onClick={() => submitForm()}
          fullWidth
          sx={{
            ...primaryButtonSx,
            minHeight: 48,
            borderRadius: "12px",
            "&.Mui-disabled": {
              bgcolor: isSaving ? BRAND.orange : undefined,
              color: isSaving ? "#fff" : undefined,
              opacity: isSaving ? 0.85 : undefined,
            },
          }}
          startIcon={
            isSaving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {isSaving ? "Submitting..." : "Submit Request"}
        </Button>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isSaving}
          fullWidth
          sx={{ minHeight: 44, textTransform: "none", fontWeight: 600, color: BRAND.muted }}
        >
          Cancel
        </Button>
      </Box>
    ) : (
      <>
        <Button onClick={handleClose} color="inherit" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isSaving}
          onClick={() => submitForm()}
          sx={{
            ...primaryButtonSx,
            minWidth: 120,
            "&.Mui-disabled": {
              bgcolor: isSaving ? BRAND.orange : undefined,
              color: isSaving ? "#fff" : undefined,
              opacity: isSaving ? 0.85 : undefined,
            },
          }}
          startIcon={
            isSaving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {isSaving ? "Submitting..." : "Submit"}
        </Button>
      </>
    );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        submitForm,
        setFieldValue,
        handleChange,
        handleBlur,
      }) => {
        const isSaving = isSubmitting || isLoding;

        return (
          <Form style={{ display: "contents" }}>
            <AutoCalculateLeaveDays />
            <FormDrawer
              open={show}
              onClose={handleClose}
              title="Request Leave"
              subtitle="Submit a new leave application for approval"
              headerMeta={isMobile ? "Fill in the details below" : undefined}
              footer={renderFooter(isSaving, submitForm)}
            >
              <Grid container spacing={gridSpacing}>
                {/* Leave type */}
                <Grid item xs={12}>
                  <SectionCard
                    title="Leave Details"
                    icon={<EventNoteOutlinedIcon sx={{ fontSize: 16 }} />}
                    isMobile={isMobile}
                  >
                    {isMobile ? (
                      <MobileSelectField
                        label="Leave Type *"
                        value={values.leave_type}
                        onChange={(val) => setFieldValue("leave_type", val)}
                        options={LEAVE_TYPE_OPTIONS}
                        sheetTitle="Select leave type"
                        sheetSubtitle="Choose paid, unpaid, or sick leave"
                      />
                    ) : (
                      <TextField
                        select
                        label="Leave Type"
                        name="leave_type"
                        value={values.leave_type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        required
                        error={touched.leave_type && Boolean(errors.leave_type)}
                        helperText={touched.leave_type && errors.leave_type}
                        sx={fieldSx}
                      >
                        {LEAVE_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    {isMobile && touched.leave_type && errors.leave_type && (
                      <Typography sx={{ fontSize: "0.75rem", color: "#B91C1C", mt: 0.5 }}>
                        {errors.leave_type}
                      </Typography>
                    )}
                  </SectionCard>
                </Grid>

                {/* Duration */}
                <Grid item xs={12}>
                  <SectionCard
                    title="Duration"
                    icon={<EventNoteOutlinedIcon sx={{ fontSize: 16 }} />}
                    isMobile={isMobile}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          type="date"
                          label="Start Date *"
                          name="leave_start_date"
                          value={values.leave_start_date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          required
                          InputLabelProps={{ shrink: true }}
                          error={
                            touched.leave_start_date && Boolean(errors.leave_start_date)
                          }
                          helperText={
                            touched.leave_start_date && errors.leave_start_date
                          }
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {isMobile ? (
                          <MobileSelectField
                            label="Start Session"
                            value={values.start_session}
                            onChange={(val) => setFieldValue("start_session", val)}
                            options={SESSION_OPTIONS}
                            sheetTitle="Start session"
                          />
                        ) : (
                          <TextField
                            select
                            label="Start Session"
                            name="start_session"
                            value={values.start_session}
                            onChange={handleChange}
                            fullWidth
                            sx={fieldSx}
                          >
                            <MenuItem value="1">Session 1</MenuItem>
                            <MenuItem value="2">Session 2</MenuItem>
                          </TextField>
                        )}
                      </Grid>

                      <Grid item xs={12} sm={8}>
                        <TextField
                          type="date"
                          label="End Date *"
                          name="leave_end_date"
                          value={values.leave_end_date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          required
                          InputLabelProps={{ shrink: true }}
                          error={touched.leave_end_date && Boolean(errors.leave_end_date)}
                          helperText={touched.leave_end_date && errors.leave_end_date}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {isMobile ? (
                          <MobileSelectField
                            label="End Session"
                            value={values.end_session}
                            onChange={(val) => setFieldValue("end_session", val)}
                            options={SESSION_OPTIONS}
                            sheetTitle="End session"
                          />
                        ) : (
                          <TextField
                            select
                            label="End Session"
                            name="end_session"
                            value={values.end_session}
                            onChange={handleChange}
                            fullWidth
                            sx={fieldSx}
                          >
                            <MenuItem value="1">Session 1</MenuItem>
                            <MenuItem value="2">Session 2</MenuItem>
                          </TextField>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: BRAND.orangeLight,
                            border: `1px solid ${BRAND.orangeHover}33`,
                            borderRadius: "10px",
                            px: 1.5,
                            py: 1.15,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              color: BRAND.navy,
                            }}
                          >
                            Total Leave Days
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "1.25rem",
                              fontWeight: 800,
                              color: BRAND.orangeHover,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {values.total_leave_days !== "" &&
                            values.total_leave_days != null
                              ? values.total_leave_days
                              : "—"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </SectionCard>
                </Grid>

                {/* Reason */}
                <Grid item xs={12}>
                  <SectionCard
                    title="Reason"
                    icon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
                    isMobile={isMobile}
                  >
                    <TextField
                      label="Reason for leave *"
                      name="reason"
                      multiline
                      rows={isMobile ? 4 : 4}
                      value={values.reason}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      required
                      placeholder="Briefly describe why you need this leave..."
                      error={touched.reason && Boolean(errors.reason)}
                      helperText={
                        touched.reason && errors.reason
                          ? errors.reason
                          : `${(values.reason || "").length} characters (min 10)`
                      }
                      FormHelperTextProps={{
                        sx: {
                          color:
                            touched.reason && errors.reason ? "#B91C1C" : BRAND.muted,
                          fontSize: "0.75rem",
                        },
                      }}
                      sx={fieldSx}
                    />
                  </SectionCard>
                </Grid>

                {/* Attachments */}
                <Grid item xs={12}>
                  <SectionCard
                    title="Attachments"
                    icon={<AttachFileOutlinedIcon sx={{ fontSize: 16 }} />}
                    isMobile={isMobile}
                    mb={0}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        borderStyle: "dashed",
                        borderWidth: 2,
                        borderColor: BRAND.orangeHover,
                        color: BRAND.orangeHover,
                        py: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                        bgcolor: BRAND.orangeLight,
                        "&:hover": {
                          borderColor: BRAND.orangeHover,
                          bgcolor: "rgba(255,106,0,0.12)",
                          borderStyle: "dashed",
                        },
                      }}
                    >
                      <CloudUploadOutlinedIcon sx={{ fontSize: 28 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
                        Tap to upload files
                      </Typography>
                      <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
                        PDF, JPG, JPEG, PNG
                      </Typography>
                      <input
                        name="attachments"
                        type="file"
                        accept="image/*,.pdf"
                        multiple
                        hidden
                        onChange={(event) => {
                          setFieldValue("attachments", event.currentTarget.files);
                        }}
                      />
                    </Button>

                    {values.attachments?.length > 0 && (
                      <Box sx={{ mt: 1.25, display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {Array.from(values.attachments).map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              p: 1,
                              border: `1px solid ${BRAND.border}`,
                              borderRadius: "10px",
                              bgcolor: "#FAFBFC",
                            }}
                          >
                            <AttachFileOutlinedIcon
                              sx={{ color: BRAND.orangeHover, fontSize: 20, flexShrink: 0 }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: "0.8125rem",
                                  color: BRAND.navy,
                                  fontWeight: 600,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {file.name}
                              </Typography>
                              <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </SectionCard>
                </Grid>
              </Grid>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default LeaveRequestFormModal;
